import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { appRepository, AppState } from '../../core/storage/repository';
import { speechProvider } from '../../core/speech/speechProvider';
import { RaraMascot } from '../../components/mascot/RaraMascot';
import { PWAInstallButton } from '../../components/pwa/PWAInstallButton';
import { MasteryBadgesSection } from './components/MasteryBadgesSection';
import { MASTERY_BADGES } from '../../core/badges/masteryBadges';

interface ChildHomeProps {
  onNavigate: (view: 'alphabet' | 'writing' | 'syllables' | 'wordbuilder' | 'camera' | 'mywords' | 'games' | 'garden' | 'parent') => void;
}

const AnimatedAlphabetIcon: React.FC = () => {
  const letters = [
    { letter: 'A', color: '#4F7CF7', delay: 0 },
    { letter: 'B', color: '#FFB84D', delay: 1.2 },
    { letter: 'C', color: '#4BC27A', delay: 2.4 },
  ];

  return (
    <motion.svg
      viewBox="0 0 96 96"
      className="w-16 h-16 md:w-20 md:h-20 mb-3"
      role="img"
      aria-label="Huruf A, B, C yang sedang melambai"
    >
      <circle cx="48" cy="48" r="44" fill="#EFF6FF" />
      <motion.path
        d="M18 23 Q12 16 17 11"
        stroke="#FFB84D"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
        animate={{ rotate: [0, 16, -8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{ originX: '17px', originY: '23px' }}
      />
      {letters.map(({ letter, color, delay }, index) => (
        <motion.g
          key={letter}
          animate={{ opacity: [0, 1, 1, 0], y: [10, 0, -2, 10], rotate: [-8, 0, 7, -8] }}
          transition={{ duration: 3.6, repeat: Infinity, delay, ease: 'easeInOut' }}
          style={{ originX: `${24 + index * 24}px`, originY: '61px' }}
        >
          <rect x={12 + index * 24} y="28" width="22" height="34" rx="8" fill={color} />
          <text
            x={23 + index * 24}
            y="52"
            textAnchor="middle"
            fill="white"
            fontFamily="Fredoka, sans-serif"
            fontSize="20"
            fontWeight="900"
          >
            {letter}
          </text>
        </motion.g>
      ))}
      <motion.circle
        cx="78"
        cy="20"
        r="3"
        fill="#FFD700"
        animate={{ scale: [0.7, 1.2, 0.7], opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      />
    </motion.svg>
  );
};

const AnimatedWritingIcon: React.FC = () => (
  <motion.svg
    viewBox="0 0 96 96"
    className="w-16 h-16 md:w-20 md:h-20 mb-3"
    role="img"
    aria-label="Tangan memegang pensil menulis huruf A lalu Z"
  >
    <rect x="7" y="10" width="82" height="76" rx="22" fill="#FFF7E8" />
    <motion.path
      d="M22 66 L34 28 L46 66 M27 53 H41"
      fill="none"
      stroke="#FFB84D"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      pathLength={1}
      strokeDasharray="1"
      animate={{ strokeDashoffset: [1, 1, 0, 0, 1] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', times: [0, 0.08, 0.4, 0.48, 1] }}
    />
    <motion.path
      d="M55 30 H78 L55 66 H78"
      fill="none"
      stroke="#4F7CF7"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      pathLength={1}
      strokeDasharray="1"
      animate={{ strokeDashoffset: [1, 1, 1, 1, 0, 0, 1] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', times: [0, 0.08, 0.4, 0.48, 0.78, 0.86, 1] }}
    />
    <motion.g
      animate={{ x: [0, 0, 0, 33, 33, 33, 0], y: [7, 7, 7, 9, 9, 9, 7], rotate: [-28, -18, -28, -12, -20, -12, -28] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', times: [0, 0.08, 0.4, 0.48, 0.78, 0.86, 1] }}
      style={{ originX: '30px', originY: '55px' }}
    >
      <rect x="25" y="48" width="25" height="6" rx="3" fill="#F59E0B" />
      <polygon points="50,48 57,51 50,54" fill="#F3C892" />
      <polygon points="57,51 60,51 57,52" fill="#243047" />
      <path d="M20 60 Q25 48 34 51 L41 59 Q37 68 28 67 Q22 66 20 60 Z" fill="#FFB84D" stroke="#E69500" strokeWidth="2" />
      <path d="M29 54 Q34 57 37 62" stroke="#E69500" strokeWidth="2" strokeLinecap="round" fill="none" />
    </motion.g>
    <motion.circle
      cx="79"
      cy="19"
      r="3"
      fill="#4BC27A"
      animate={{ scale: [0.7, 1.2, 0.7], opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
    />
  </motion.svg>
);

const AnimatedSyllableIcon: React.FC = () => (
  <motion.svg
    viewBox="0 0 96 96"
    className="w-16 h-16 md:w-20 md:h-20 mb-3"
    role="img"
    aria-label="Buku membuka dan menutup halamannya"
  >
    <rect x="7" y="10" width="82" height="76" rx="22" fill="#ECFDF5" />
    <path d="M48 28 V72" stroke="#10B981" strokeWidth="3" strokeLinecap="round" />
    <motion.path
      d="M48 27 C38 22 27 22 18 27 V70 C28 66 38 66 48 72 Z"
      fill="#6EE7B7"
      stroke="#059669"
      strokeWidth="3"
      animate={{ scaleX: [1, 0.34, 1.12, 1] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', times: [0, 0.28, 0.62, 1] }}
      style={{ originX: '48px', originY: '50px' }}
    />
    <motion.path
      d="M48 27 C58 22 69 22 78 27 V70 C68 66 58 66 48 72 Z"
      fill="#A7F3D0"
      stroke="#059669"
      strokeWidth="3"
      animate={{ scaleX: [1, 0.34, 1.12, 1] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', times: [0, 0.28, 0.62, 1] }}
      style={{ originX: '48px', originY: '50px' }}
    />
    <motion.g
      animate={{ opacity: [0.25, 0.1, 1, 0.25] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      stroke="#047857"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M25 39 H41" />
      <path d="M25 47 H40" />
      <path d="M55 39 H71" />
      <path d="M56 47 H71" />
    </motion.g>
    <motion.circle
      cx="77"
      cy="19"
      r="3"
      fill="#F59E0B"
      animate={{ scale: [0.7, 1.2, 0.7], opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
    />
  </motion.svg>
);

const AnimatedWordBuilderIcon: React.FC = () => (
  <motion.svg
    viewBox="0 0 96 96"
    className="w-16 h-16 md:w-20 md:h-20 mb-3"
    role="img"
    aria-label="Puzzle huruf berkumpul membentuk objek"
  >
    <rect x="7" y="10" width="82" height="76" rx="22" fill="#EEF2FF" />
    <motion.path
      d="M48 20 L55 35 L71 37 L59 48 L62 64 L48 56 L34 64 L37 48 L25 37 L41 35 Z"
      fill="#C7D2FE"
      animate={{ scale: [0.8, 1, 1, 0.8], opacity: [0, 0.45, 0.45, 0] }}
      transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.g
      animate={{ x: [0, 8, 8, 0], y: [8, 8, 0, 8], rotate: [-8, 0, 0, -8] }}
      transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut', times: [0, 0.28, 0.7, 1] }}
    >
      <rect x="18" y="45" width="22" height="22" rx="7" fill="#818CF8" />
      <circle cx="40" cy="56" r="4" fill="#818CF8" />
      <text x="29" y="61" textAnchor="middle" fill="white" fontFamily="Fredoka, sans-serif" fontSize="15" fontWeight="900">A</text>
    </motion.g>
    <motion.g
      animate={{ x: [0, -8, -8, 0], y: [8, 8, 0, 8], rotate: [8, 0, 0, 8] }}
      transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut', times: [0, 0.28, 0.7, 1], delay: 0.18 }}
    >
      <rect x="56" y="45" width="22" height="22" rx="7" fill="#6366F1" />
      <circle cx="56" cy="56" r="4" fill="#6366F1" />
      <text x="67" y="61" textAnchor="middle" fill="white" fontFamily="Fredoka, sans-serif" fontSize="15" fontWeight="900">B</text>
    </motion.g>
    <motion.g
      animate={{ y: [-8, -8, 0, -8], rotate: [-5, 0, 0, -5] }}
      transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut', times: [0, 0.28, 0.7, 1], delay: 0.36 }}
    >
      <rect x="37" y="21" width="22" height="22" rx="7" fill="#4F46E5" />
      <circle cx="48" cy="43" r="4" fill="#4F46E5" />
      <text x="48" y="37" textAnchor="middle" fill="white" fontFamily="Fredoka, sans-serif" fontSize="15" fontWeight="900">C</text>
    </motion.g>
  </motion.svg>
);

const AnimatedMyWordsIcon: React.FC = () => (
  <motion.svg
    viewBox="0 0 96 96"
    className="w-16 h-16 md:w-20 md:h-20 mb-3"
    role="img"
    aria-label="Buku tersusun satu per satu menjadi tumpukan"
  >
    <rect x="7" y="10" width="82" height="76" rx="22" fill="#F0FDFA" />
    {[
      { y: 56, color: '#2DD4BF', delay: 0 },
      { y: 44, color: '#14B8A6', delay: 1.1 },
      { y: 32, color: '#0F766E', delay: 2.2 },
    ].map(book => (
      <motion.g
        key={book.y}
        animate={{ y: [12, 0, 0, 12], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 3.3, repeat: Infinity, ease: 'easeInOut', delay: book.delay }}
      >
        <rect x="20" y={book.y} width="56" height="10" rx="4" fill={book.color} />
        <path d={`M28 ${book.y + 3} H68`} stroke="#CCFBF1" strokeWidth="2" strokeLinecap="round" />
        <path d={`M25 ${book.y} V${book.y + 10}`} stroke="#115E59" strokeWidth="2" />
      </motion.g>
    ))}
    <motion.circle
      cx="78"
      cy="21"
      r="3"
      fill="#F59E0B"
      animate={{ scale: [0.7, 1.2, 0.7], opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
    />
  </motion.svg>
);

const AnimatedGamesIcon: React.FC = () => (
  <motion.svg
    viewBox="0 0 96 96"
    className="w-16 h-16 md:w-20 md:h-20 mb-3"
    role="img"
    aria-label="Gamepad bermain yang bergerak riang"
  >
    <rect x="7" y="10" width="82" height="76" rx="22" fill="#FAF5FF" />
    <motion.g
      animate={{ y: [2, -2, 2], rotate: [-3, 3, -3] }}
      transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
    >
      <path d="M25 40 Q27 28 38 31 H58 Q69 28 71 40 L77 60 Q79 70 69 70 Q63 70 58 59 H38 Q33 70 27 70 Q17 70 19 60 Z" fill="#A78BFA" stroke="#7C3AED" strokeWidth="3" />
      <path d="M32 43 V55 M26 49 H38" stroke="white" strokeWidth="3" strokeLinecap="round" />
      <circle cx="63" cy="46" r="4" fill="#FDE68A" />
      <circle cx="70" cy="53" r="4" fill="#FCA5A5" />
    </motion.g>
    <motion.circle
      cx="21"
      cy="23"
      r="3"
      fill="#F59E0B"
      animate={{ scale: [0.7, 1.2, 0.7], opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
    />
  </motion.svg>
);

const AnimatedChildIcon: React.FC = () => (
  <motion.svg viewBox="0 0 48 48" className="w-8 h-8" role="img" aria-label="Teman belajar">
    <circle cx="24" cy="24" r="22" fill="#FDE68A" />
    <motion.path d="M10 20 Q5 14 10 10" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" fill="none" animate={{ rotate: [0, 18, -8, 0] }} transition={{ duration: 1.8, repeat: Infinity }} style={{ originX: '10px', originY: '20px' }} />
    <circle cx="24" cy="21" r="10" fill="#FFB84D" />
    <circle cx="20" cy="21" r="1.5" fill="#243047" />
    <circle cx="28" cy="21" r="1.5" fill="#243047" />
    <path d="M20 26 Q24 29 28 26" stroke="#243047" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    <path d="M15 38 Q24 29 33 38" fill="#4F7CF7" />
  </motion.svg>
);

const AnimatedStarIcon: React.FC = () => (
  <motion.svg viewBox="0 0 28 28" className="w-6 h-6" role="img" aria-label="Bintang terkumpul">
    <motion.path d="M14 3 L17 10 L25 11 L19 16 L21 24 L14 20 L7 24 L9 16 L3 11 L11 10 Z" fill="#FBBF24" stroke="#D97706" strokeWidth="1.5" animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.12, 1] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: '14px', originY: '14px' }} />
    <motion.circle cx="23" cy="5" r="2" fill="#FFF" animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.4, repeat: Infinity }} />
  </motion.svg>
);

const AnimatedMedalIcon: React.FC = () => (
  <motion.svg viewBox="0 0 28 28" className="w-6 h-6" role="img" aria-label="Lencana prestasi">
    <path d="M9 3 H13 L14 12 L10 10 Z M19 3 H15 L14 12 L18 10 Z" fill="#4F7CF7" />
    <motion.circle cx="14" cy="18" r="7" fill="#FBBF24" stroke="#D97706" strokeWidth="1.5" animate={{ rotate: [0, -8, 8, 0] }} transition={{ duration: 2.4, repeat: Infinity }} style={{ originX: '14px', originY: '11px' }} />
    <path d="M14 14 L15.2 16.5 L18 16.8 L16 18.7 L16.5 21.5 L14 20.2 L11.5 21.5 L12 18.7 L10 16.8 L12.8 16.5 Z" fill="#FFF7E8" />
  </motion.svg>
);

const AnimatedLockIcon: React.FC = () => (
  <motion.svg viewBox="0 0 24 24" className="w-5 h-5" role="img" aria-label="Area orang tua">
    <rect x="5" y="10" width="14" height="11" rx="3" fill="#94A3B8" />
    <motion.path d="M8 10 V7 A4 4 0 0 1 16 7 V10" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" animate={{ y: [0, -1, 0] }} transition={{ duration: 2, repeat: Infinity }} />
    <circle cx="12" cy="15" r="1.5" fill="white" />
  </motion.svg>
);

const AnimatedCameraIcon: React.FC = () => (
  <motion.svg viewBox="0 0 40 40" className="w-9 h-9" role="img" aria-label="Kamera membaca">
    <rect x="5" y="11" width="30" height="21" rx="5" fill="#FFF" stroke="#F97316" strokeWidth="2" />
    <path d="M12 11 L15 7 H24 L28 11" fill="#FFF" stroke="#F97316" strokeWidth="2" strokeLinejoin="round" />
    <motion.circle cx="20" cy="21.5" r="6" fill="#FDBA74" stroke="#EA580C" strokeWidth="2" animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 2, repeat: Infinity }} />
    <motion.circle cx="18" cy="19.5" r="1.5" fill="#FFF" animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.3, repeat: Infinity }} />
  </motion.svg>
);

export const ChildHome: React.FC<ChildHomeProps> = ({ onNavigate }) => {
  const [state, setState] = useState<AppState>(appRepository.getState());
  const [mascotMessage, setMascotMessage] = useState('Halo! Mau belajar apa hari ini?');
  const [isBadgesOpen, setIsBadgesOpen] = useState(false);

  useEffect(() => {
    const unsub = appRepository.subscribe(s => setState(s));
    return unsub;
  }, []);

  const unlockedCount = MASTERY_BADGES.filter(b => b.isUnlocked(state)).length;

  const handleMascotGreeting = () => {
    const text = `Halo ${state.profile.nickname}! Ayo belajar membaca dan raih lencana prestasimu bersama Rara!`;
    setMascotMessage(text);
    speechProvider.speak(text, { rate: 0.88, pitch: 1.15 });
  };

  const handleNavigateWithSound = (view: 'alphabet' | 'writing' | 'syllables' | 'wordbuilder' | 'camera' | 'mywords' | 'games' | 'garden' | 'parent') => {
    speechProvider.playChime('tap');
    onNavigate(view);
  };

  const handleToggleBadges = () => {
    setIsBadgesOpen(prev => {
      const next = !prev;
      if (next) {
        setTimeout(() => {
          document.getElementById('mastery-badges-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFDF8] pb-12 select-none">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-2 border-amber-100 px-4 py-3 flex items-center justify-between">
        {/* Child Avatar & Greeting */}
        <div
          onClick={handleMascotGreeting}
          className="flex items-center gap-2.5 cursor-pointer active:scale-95 transition"
        >
          <div className="w-11 h-11 bg-amber-200 border-2 border-amber-400 rounded-full flex items-center justify-center text-xl shadow-xs">
            <AnimatedChildIcon />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              Halo, Teman!
            </div>
            <div className="text-base md:text-lg font-display font-extrabold text-[#243047] leading-tight">
              {state.profile.nickname}
            </div>
          </div>
        </div>

        {/* Stars, Badges, PWA Install & Parent Gate Link */}
        <div className="flex items-center gap-2 md:gap-2.5">
          {/* Star Counter */}
          <div
            onClick={() => {
              speechProvider.playChime('celebrate');
              speechProvider.speak(`Kamu sudah mengumpulkan ${state.profile.stars} bintang! Hebat!`);
            }}
            className="flex items-center gap-1.5 bg-amber-50 border-2 border-amber-300 px-3 py-1.5 rounded-2xl shadow-xs cursor-pointer active:scale-95"
            title="Bintang Terkumpul"
          >
            <AnimatedStarIcon />
            <span className="font-display font-black text-amber-900 text-sm md:text-base">
              {state.profile.stars}
            </span>
          </div>

          {/* Badges Counter Pill */}
          <div
            onClick={() => {
              speechProvider.playChime('celebrate');
              handleToggleBadges();
              speechProvider.speak(`Kamu sudah mengoleksi ${unlockedCount} lencana prestasi! Sentuh untuk melihatnya.`);
            }}
            className="flex items-center gap-1.5 bg-yellow-50 hover:bg-yellow-100 border-2 border-yellow-400 px-3 py-1.5 rounded-2xl shadow-xs cursor-pointer active:scale-95 transition"
            title="Lencana Prestasi Terbuka"
          >
            <AnimatedMedalIcon />
            <span className="font-display font-black text-amber-950 text-sm md:text-base">
              {unlockedCount}
            </span>
          </div>

          {/* PWA In-App Install Prompt Button */}
          <PWAInstallButton />

          {/* Parent Gate Button */}
          <button
            onClick={() => handleNavigateWithSound('parent')}
            className="flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-2xl transition active:scale-95 cursor-pointer"
            title="Area Orang Tua"
          >
            <AnimatedLockIcon />
            <span className="hidden sm:inline">Orang Tua</span>
          </button>
        </div>
      </header>

      {/* Main Home Content */}
      <main className="max-w-4xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-6">
        {/* Friendly Mascot Companion */}
        <div className="pt-2">
          <RaraMascot
            state="wave"
            message={mascotMessage}
            size="md"
            onBubbleClick={handleMascotGreeting}
          />
        </div>

        {/* Signature Feature Banner: Ayo Baca Kamera */}
        <div
          onClick={() => handleNavigateWithSound('camera')}
          className="bg-linear-to-r from-amber-400 via-orange-400 to-amber-500 rounded-3xl p-6 md:p-8 text-white shadow-xl cursor-pointer hover:shadow-2xl transition-all transform active:scale-98 relative overflow-hidden border-4 border-amber-300"
        >
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="max-w-md">
              <span className="bg-white/20 backdrop-blur-xs text-white text-xs font-extrabold uppercase px-3 py-1 rounded-full mb-2 inline-block">
                ✨ Fitur Istimewa
              </span>
              <h2 className="text-2xl md:text-3xl font-display font-black tracking-wide leading-tight">
                Ayo Baca dengan Kamera!
              </h2>
              <p className="text-sm md:text-base text-amber-100 mt-1 font-medium">
                Foto rambu jalan, buku, atau kemasan di sekitarmu. Rara akan membacakan suku katanya!
              </p>
            </div>
            <button className="h-16 px-8 rounded-full bg-white text-orange-600 font-display font-black text-lg md:text-xl shadow-lg flex items-center gap-3 shrink-0 active:scale-95 transition cursor-pointer">
              <AnimatedCameraIcon />
              <span>Buka Kamera</span>
            </button>
          </div>
        </div>

        {/* Primary Learning Activity Grid */}
        <div>
          <h3 className="text-base font-bold text-[#64748B] mb-3">
            Pilihan Belajar Membaca:
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {/* 1. Huruf */}
            <button
              onClick={() => handleNavigateWithSound('alphabet')}
              className="bg-white rounded-3xl p-5 md:p-6 shadow-md border-3 border-blue-200 hover:border-blue-400 flex flex-col items-center justify-center text-center transition-all active:scale-95 cursor-pointer"
            >
              <AnimatedAlphabetIcon />
              <h4 className="text-lg md:text-xl font-display font-black text-[#243047]">
                Huruf A-Z
              </h4>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">
                Kenal bentuk & bunyinya
              </p>
            </button>

            {/* 2. Menulis Huruf (PixiJS Interaktif) */}
            <button
              onClick={() => handleNavigateWithSound('writing')}
              className="bg-white rounded-3xl p-5 md:p-6 shadow-md border-3 border-amber-300 hover:border-amber-500 flex flex-col items-center justify-center text-center transition-all active:scale-95 cursor-pointer relative"
            >
              <AnimatedWritingIcon />
              <h4 className="text-lg md:text-xl font-display font-black text-[#243047]">
                Menulis Huruf
              </h4>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">
                Panduan urutan garis A-Z
              </p>
            </button>

            {/* 3. Suku Kata */}
            <button
              onClick={() => handleNavigateWithSound('syllables')}
              className="bg-white rounded-3xl p-5 md:p-6 shadow-md border-3 border-emerald-200 hover:border-emerald-400 flex flex-col items-center justify-center text-center transition-all active:scale-95 cursor-pointer"
            >
              <AnimatedSyllableIcon />
              <h4 className="text-lg md:text-xl font-display font-black text-[#243047]">
                Suku Kata
              </h4>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">
                BA • BI • BU • BE • BO
              </p>
            </button>

            {/* 3. Susun Kata */}
            <button
              onClick={() => handleNavigateWithSound('wordbuilder')}
              className="bg-white rounded-3xl p-5 md:p-6 shadow-md border-3 border-indigo-200 hover:border-indigo-400 flex flex-col items-center justify-center text-center transition-all active:scale-95 cursor-pointer"
            >
              <AnimatedWordBuilderIcon />
              <h4 className="text-lg md:text-xl font-display font-black text-[#243047]">
                Susun Kata
              </h4>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">
                BA + JU = BAJU
              </p>
            </button>

            {/* 4. Kataku */}
            <button
              onClick={() => handleNavigateWithSound('mywords')}
              className="bg-white rounded-3xl p-5 md:p-6 shadow-md border-3 border-teal-200 hover:border-teal-400 flex flex-col items-center justify-center text-center transition-all active:scale-95 cursor-pointer"
            >
              <AnimatedMyWordsIcon />
              <h4 className="text-lg md:text-xl font-display font-black text-[#243047]">
                Kataku ({state.discoveredWords.length})
              </h4>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">
                Koleksi kata temuanmu
              </p>
            </button>

            {/* 5. Bermain Games */}
            <button
              onClick={() => handleNavigateWithSound('games')}
              className="bg-white rounded-3xl p-5 md:p-6 shadow-md border-3 border-purple-200 hover:border-purple-400 flex flex-col items-center justify-center text-center transition-all active:scale-95 cursor-pointer"
            >
              <AnimatedGamesIcon />
              <h4 className="text-lg md:text-xl font-display font-black text-[#243047]">
                Bermain
              </h4>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">
                Panggung grafis interaktif
              </p>
            </button>

          </div>
        </div>

        {/* Mastery Badges System (Collapsible Bottom Section) */}
        <MasteryBadgesSection
          state={state}
          isOpen={isBadgesOpen}
          onToggle={handleToggleBadges}
          onNavigate={onNavigate}
        />
      </main>
    </div>
  );
};
